import { ReactNode, useMemo, useState } from 'react'
import {
  ButtonV2,
  StatusChipBg,
  StatusChipText,
  StatusChipV3,
} from '@labrav/react-components'
import { FundingSetupContext } from '../../types'
import { useStyles } from './FundingBenefitsSetupPage.styles'

const SUPPORT_CC = 'support+funding@labra.io'
const SUPPORT_ENABLE = 'support@labra.io'

/** Labra partner / account UUIDs must not appear as the AWS Partner / SPMS id. */
const LABRA_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const field = (value: string | undefined, fallback: string) => {
  const trimmed = value?.trim()
  return { text: trimmed || fallback, isPlaceholder: !trimmed }
}

/** Prefer SPMS / AWS Partner ids; drop UUID-shaped Labra ids. */
const awsPartnerIdField = (value: string | undefined) => {
  const trimmed = value?.trim()
  if (!trimmed || LABRA_UUID_RE.test(trimmed)) {
    return field(undefined, 'Partner ID')
  }
  return field(trimmed, 'Partner ID')
}

const FieldText = ({
  text,
  isPlaceholder,
  className,
}: {
  text: string
  isPlaceholder: boolean
  className: string
}) => (
  <span className={isPlaceholder ? className : undefined}>{text}</span>
)

export interface FundingBenefitsSetupPageProps {
  mpoppGrowWalletFound?: boolean
  setupContext?: FundingSetupContext
}

export const FundingBenefitsSetupPage = ({
  mpoppGrowWalletFound = false,
  setupContext,
}: FundingBenefitsSetupPageProps) => {
  const { classes } = useStyles()
  const [copied, setCopied] = useState(false)

  const company = field(setupContext?.companyName, 'Company name')
  const partnerId = awsPartnerIdField(setupContext?.awsPartnerId)
  const accountId = field(
    setupContext?.partnerCentralAccountId,
    'Partner Central account ID'
  )
  const contactName = field(setupContext?.contactName, 'Your name')
  const contactTitle = field(setupContext?.contactTitle, 'Your title')
  const contactEmail = field(setupContext?.contactEmail, 'your@email.example')
  const walletMissing = !mpoppGrowWalletFound

  const { subject, body, mailto } = useMemo(() => {
    const subjectLine =
      `Request: Benefits API access` +
      (walletMissing ? ' + MPOPP Grow wallet' : '') +
      ` for ${company.text} (Partner ID ${partnerId.text})`

    const bodyLines = [
      `Hi [PDM name],`,
      ``,
      `Could you help us enable the following on our AWS Partner Central account?`,
      ``,
      `Account details`,
      `- Company: ${company.text}`,
      `- AWS Partner ID: ${partnerId.text}`,
      `- Partner Central account ID: ${accountId.text}`,
      `- Primary contact: ${contactName.text}, ${contactTitle.text}, ${contactEmail.text}`,
      ``,
      `Requests`,
      `1. Enable Benefits API access for our Partner Central account. We use Labra to manage our AWS funding requests, and it needs this access to view our eligible benefits and submit and track requests.`,
    ]
    if (walletMissing) {
      bodyLines.push(
        `2. Provision an MPOPP Grow wallet for our account so we can access Marketplace Private Offer Promotion Program funds for upcoming private offers.`
      )
    }
    bodyLines.push(
      ``,
      `Let us know if you need anything else from us.`,
      ``,
      `Thanks,`,
      contactName.text,
      `${contactTitle.text}, ${company.text}`
    )
    const bodyText = bodyLines.join('\n')
    const href =
      `mailto:?cc=${encodeURIComponent(SUPPORT_CC)}` +
      `&subject=${encodeURIComponent(subjectLine)}` +
      `&body=${encodeURIComponent(bodyText)}`
    return { subject: subjectLine, body: bodyText, mailto: href }
  }, [
    accountId.text,
    company.text,
    contactEmail.text,
    contactName.text,
    contactTitle.text,
    partnerId.text,
    walletMissing,
  ])

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore clipboard failures
    }
  }

  const renderField = (f: { text: string; isPlaceholder: boolean }): ReactNode => (
    <FieldText text={f.text} isPlaceholder={f.isPlaceholder} className={classes.ph} />
  )

  return (
    <div
      className={classes.root}
      data-testid="funding-benefits-setup-page"
    >
      <section className={classes.card}>
        <div className={classes.intro}>
          <div className={classes.eyebrow}>Setup needed</div>
          <h2 className={classes.title}>
            Enable AWS Benefits API access to get started
          </h2>
          <p className={classes.body}>
            Labra needs it to find and file your AWS funding. Your Partner
            Development Manager (PDM) can turn it on.
          </p>
          <div className={classes.pills}>
            <StatusChipV3
              label="Benefits API: not enabled"
              bgColor={StatusChipBg.RED}
              textColor={StatusChipText.RED}
              sizeVarient="normal"
            />
            {walletMissing ? (
              <StatusChipV3
                label="MPOPP Grow wallet: not found"
                bgColor={StatusChipBg.ORANGE}
                textColor={StatusChipText.ORANGE}
                sizeVarient="normal"
              />
            ) : (
              <StatusChipV3
                label="MPOPP Grow wallet: active"
                bgColor={StatusChipBg.GREEN}
                textColor={StatusChipText.GREEN}
                sizeVarient="normal"
              />
            )}
          </div>
        </div>

        <div className={classes.content}>
          <div className={classes.stepsCol}>
            <h3 className={classes.stepsTitle}>How to get set up</h3>
            <ol className={classes.stepsList}>
              <li className={classes.stepItem}>
                <span className={classes.stepNum}>1</span>
                <div className={classes.stepText}>
                  <strong>Copy the email</strong> and fill in the{' '}
                  <span className={classes.ph}>highlighted</span> fields.
                </div>
              </li>
              <li className={classes.stepItem}>
                <span className={classes.stepNum}>2</span>
                <div className={classes.stepText}>
                  <strong>Send it to your PDM (Partner Development Manager)</strong>{' '}
                  with Labra in CC.
                </div>
              </li>
              <li className={classes.stepItem}>
                <span className={classes.stepNum}>3</span>
                <div className={classes.stepText}>
                  <strong>You&apos;re done.</strong> This page updates when you
                  come back after access is enabled.
                </div>
              </li>
            </ol>
          </div>

          <div className={classes.emailCol}>
            <div className={classes.emailHeader}>
              <div className={classes.emailHeaderTitle}>Email to your PDM</div>
              <div className={classes.emailActions}>
                <ButtonV2
                  styleType="outlined"
                  smallVariant
                  href={mailto}
                  component="a"
                  data-testid="open-pdm-email"
                >
                  Open in email app
                </ButtonV2>
                <ButtonV2
                  styleType="primary"
                  smallVariant
                  onClick={() => void copyEmail()}
                  data-testid="copy-pdm-email"
                >
                  {copied ? 'Copied' : 'Copy email'}
                </ButtonV2>
              </div>
            </div>

            <div className={classes.metaRows}>
              <div className={classes.metaRow}>
                <span className={classes.metaLabel}>To</span>
                <span className={classes.ph}>PDM email</span>
              </div>
              <div className={classes.metaRow}>
                <span className={classes.metaLabel}>Cc</span>
                <span>{SUPPORT_CC}</span>
              </div>
              <div className={classes.metaRow}>
                <span className={classes.metaLabel}>Subject</span>
                <span>
                  Request: Benefits API access
                  {walletMissing ? ' + MPOPP Grow wallet' : ''} for{' '}
                  {renderField(company)} (Partner ID {renderField(partnerId)})
                </span>
              </div>
            </div>

            <div className={classes.emailBody}>
              <p>
                Hi <span className={classes.ph}>PDM name</span>,
              </p>
              <p>
                Could you help us enable the following on our AWS Partner
                Central account?
              </p>
              <div>
                <div className={classes.sectionLabel}>Account details</div>
                <ul>
                  <li>Company: {renderField(company)}</li>
                  <li>AWS Partner ID: {renderField(partnerId)}</li>
                  <li>Partner Central account ID: {renderField(accountId)}</li>
                  <li>
                    Primary contact: {renderField(contactName)},{' '}
                    {renderField(contactTitle)}, {renderField(contactEmail)}
                  </li>
                </ul>
              </div>
              <div>
                <div className={classes.sectionLabel}>Requests</div>
                <ol>
                  <li>
                    Enable Benefits API access for our Partner Central account.
                    We use Labra to manage our AWS funding requests, and it
                    needs this access to view our eligible benefits and submit
                    and track requests.
                  </li>
                  {walletMissing ? (
                    <li>
                      Provision an MPOPP Grow wallet for our account so we can
                      access Marketplace Private Offer Promotion Program funds
                      for upcoming private offers.
                    </li>
                  ) : null}
                </ol>
              </div>
              <p>Let us know if you need anything else from us.</p>
              <p>
                Thanks,
                <br />
                {renderField(contactName)}
                <br />
                {renderField(contactTitle)}, {renderField(company)}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export interface FundingIamDeniedCtaProps {
  /** Optional className for embedding in dashboard banners. */
  className?: string
  compact?: boolean
}

/** Compact CTA when IAM policy denies ListBenefits (or role is missing). */
export const FundingIamDeniedCta = ({
  className,
  compact = false,
}: FundingIamDeniedCtaProps) => {
  const { classes, cx } = useStyles()
  if (compact) {
    return (
      <span className={className} data-testid="funding-iam-denied-cta">
        Email{' '}
        <a className={classes.iamLink} href={`mailto:${SUPPORT_ENABLE}`}>
          {SUPPORT_ENABLE}
        </a>{' '}
        to enable Funding management.
      </span>
    )
  }
  return (
    <div
      className={cx(classes.iamRoot, className)}
      data-testid="funding-iam-denied-cta"
    >
      <h2 className={classes.iamTitle}>Funding management needs access</h2>
      <p className={classes.iamBody}>
        Email{' '}
        <a className={classes.iamLink} href={`mailto:${SUPPORT_ENABLE}`}>
          {SUPPORT_ENABLE}
        </a>{' '}
        to enable Funding management.
      </p>
    </div>
  )
}

export default FundingBenefitsSetupPage
